import { defineQueryOptions } from '@pinia/colada'
import { STALE_TIME } from '@rialtic/utils'
import type { PolicyListFilters } from '@worker/policy-builder/client'
import { policyRegistry } from '~/utils/policy-studio/registry'

/* Policy Studio registry reads (`PolicyManagerAgent`). Live workflow state — a
   policy cached in its old stage must never resurrect from IndexedDB — so all
   of them are `persist: false`, and `enabled: import.meta.client` keeps the SSR
   prefetch off the relative proxy paths. Freshness is the registry socket's
   change nudge (`usePolicyRegistry`) plus an invalidation after any local
   command; live co-editing happens in the room. */

const root = ['policy-studio', 'policies'] as const

export const POLICY_REGISTRY_KEYS = {
  /* Every registry read hangs off this, so one invalidation refreshes them all. */
  root,
  list: (filters: PolicyListFilters) => [...root, 'list', filters] as const,
  summary: (id: string) => [...root, 'summary', id] as const,
  counts: [...root, 'counts'] as const,
  mentions: (tags: readonly string[]) => [...root, 'mentions', tags] as const,
}

export const policyListQuery = defineQueryOptions(
  (filters: PolicyListFilters) => ({
    key: POLICY_REGISTRY_KEYS.list(filters),
    query: () => policyRegistry.listAll(filters),
    enabled: import.meta.client,
    staleTime: STALE_TIME.THIRTY_SECONDS,
    persist: false,
  }),
)

/** One policy's row. A 404 here is how a detail page knows the id does not exist. */
export const policySummaryQuery = defineQueryOptions((id: string) => ({
  key: POLICY_REGISTRY_KEYS.summary(id),
  query: () => policyRegistry.get(id),
  enabled: import.meta.client && Boolean(id),
  staleTime: STALE_TIME.THIRTY_SECONDS,
  persist: false,
}))

export const policyCountsQuery = defineQueryOptions({
  key: POLICY_REGISTRY_KEYS.counts,
  query: () => policyRegistry.counts(),
  enabled: import.meta.client,
  staleTime: STALE_TIME.THIRTY_SECONDS,
  persist: false,
})

/** Open threads that tag any of `tags`; idle until there is a tag to match. */
export const policyMentionsQuery = defineQueryOptions((tags: string[]) => ({
  key: POLICY_REGISTRY_KEYS.mentions(tags),
  query: () => policyRegistry.mentions(tags),
  enabled: import.meta.client && tags.length > 0,
  staleTime: STALE_TIME.THIRTY_SECONDS,
  persist: false,
}))
